/* eslint-disable @typescript-eslint/naming-convention */
import { ActivatedRouteSnapshot, CanActivateFn, Router, RouterStateSnapshot, UrlTree } from '@angular/router';
import { BehaviorSubject, Observable, combineLatest, from, of, throwError} from 'rxjs';
import { catchError, concatMap, first, map, mergeMap, shareReplay, tap} from 'rxjs/operators';
import { Location } from '@angular/common';
import { environment } from '../../environments/environment';
import { Auth0Client, createAuth0Client } from '@auth0/auth0-spa-js';
import { Injectable, inject } from '@angular/core';

@Injectable()
export class AuthService  {
  private _config;
  private _internalOnly = Boolean(JSON.parse(environment.internalOnly));
  private _user = new BehaviorSubject<any>(null);

  get user() {
    return this._user.value;
  }
  user$ = this._user.asObservable();

  public routeFrom: string;
  public routeTo: string;
  auth0Client$: Observable<Auth0Client>;
  isAuthenticated$;
  handleRedirectCallback$;
  loggedIn: boolean = null;
  auth0Claims = environment.auth0Claims;

  constructor(
    private router: Router,
    private _location: Location
  ) {
    this._config = environment.alleAuthConfig;

    this.setupAuth0();

    this.isAuthenticated$ = this.auth0Client$.pipe(
      concatMap((client: Auth0Client) => from(client.isAuthenticated())),
      tap((res) => {
        this.loggedIn = res;
      })
    );
    this.handleRedirectCallback$ = this.auth0Client$.pipe(
      concatMap((client: Auth0Client) => from(client.handleRedirectCallback()))
    );
  }

  canActivate(
    next: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): Observable<boolean> | Promise<boolean | UrlTree> | boolean {
    return this.isAuthenticated$.pipe(
      mergeMap((loggedIn) => {
        if (!loggedIn) {
          this.login(state.url);
          return of(false);
        } else {
          return this.checkTokenClaims$();
        }
      }),
      mergeMap((checkTokenResult) => {
        if (!checkTokenResult) {
          this.router.navigate(['/auth-error']);
          return of(false);
        } else {
          return of(true);
        }
      })
    );
  }

  checkIsAppManager$() {
    return this.user$.pipe(
      first(),
      map((user) => {
        if (user) {
          return user.isAppManager;
        }
      }),
      catchError((error) => of(false))
    );
  }

  checkTokenClaims$() {
    const url = this._location.path();
    return this.getUser$().pipe(
      first(),
      map((user) => {
        if (user) {
          if (user.isExternal && this._internalOnly === false) {
            if (url === '/users' || url === '/invites') {
              return false;
            }
            return user.isCompanyManager;
          } else {
            return user.isAppManager || user.isAppOwner || user.isInternalUser;
          }
        }
      }),
      catchError((error) => {
        console.log(error);
        return of(false);
      })
    );
  }

  getTokenSilently$(options?): Observable<any> {
    return this.auth0Client$.pipe(
      concatMap((client: Auth0Client) => from(client.getTokenSilently(options)))
    );
  }

  getUser$(): Observable<any> {
    return this.auth0Client$.pipe(
      concatMap((client: Auth0Client) => from(client.getUser())),
      tap((user) => {
        if (user) {
          const roles: Array<string> = user[this.auth0Claims.roles];
          user.isExternal =
            user[this.auth0Claims.userType] === 'external';

          user.isCompanyManager = user.isExternal
            ? Boolean(user[this.auth0Claims.isCompanyManager])
            : false;

          user.isAppManager = user.isExternal
            ? false
            : Boolean(roles.includes('AppManager'));

          user.isAppOwner = user.isExternal
            ? false
            : Boolean(roles.includes('AppOwner'));

          user.isInternalUser = user.isExternal ? false : Boolean(roles.includes('User'));

          user.isInternal = user[this.auth0Claims.userType] === 'internal'

          this._user.next(user);
        }
      })
    );
  }

  public loginRedirect(): void {
    if (this.routeTo) {
      this.router.navigate([this.routeTo]);
    } else {
      this.router.navigate([this._config.inAppRedirects.loggedIn]);
    }
  }

  localAuthSetup() {
    // This should only be called on app initialization
    // Set up local authentication streams
    const checkAuth$ = this.isAuthenticated$.pipe(
      concatMap((loggedIn: boolean) => {
        if (loggedIn) {
          // If authenticated, get user and set in app
          // NOTE: you could pass options here if needed
          return this.getUser$();
        }
        // If not authenticated, return stream that emits 'false'
        return of(loggedIn);
      })
    );
    checkAuth$.subscribe();
  }

  login(redirectPath: string = '/') {
    // A desired redirect path can be passed to login method
    // (e.g., from a route guard)
    // Ensure Auth0 client instance exists
    this.auth0Client$.subscribe((client: Auth0Client) => {
      // Call method to log in
      client.loginWithRedirect({
        authorizationParams:{
          redirect_uri: this._config.redirectUri,
        },
        appState: { target: redirectPath },
      });
    });
  }

  handleAuthCallback() {
    // Call when app reloads after user logs in with Auth0
    const params = window.location.search;
    if (params.includes('code=') && params.includes('state=')) {
      let targetRoute: string; // Path to redirect to after login processsed
      const authComplete$ = this.handleRedirectCallback$.pipe(
        // Have client, now call method to handle auth callback redirect
        tap((cbRes: any) => {
          // Get and set target redirect route from callback results
          targetRoute =
            cbRes.appState && cbRes.appState.target
              ? cbRes.appState.target
              : '/';
        }),
        concatMap(() =>
          // Redirect callback complete; get user and login status
          combineLatest([this.getUser$(), this.isAuthenticated$])
        )
      );
      // Subscribe to authentication completion observable
      // Response will be an array of user and login status
      authComplete$.subscribe(([user, loggedIn]) => {
        targetRoute = targetRoute.includes("/login?iss")? "/login":targetRoute;
        // Redirect to target route after callback processing
        this.router.navigate([targetRoute]);
      });
    }
  }

  sso(redirectPath: string = '/users') {
    // A desired redirect path can be passed to login method
    // (e.g., from a route guard)
    // Ensure Auth0 client instance exists
    this.auth0Client$.subscribe((client: Auth0Client) => {
      // Call method to log in

      client.loginWithRedirect({
        authorizationParams:{
          redirect_uri: environment.alleAuthConfig.redirectUri,
          prompt: 'none',
        },
        appState: { target: redirectPath },
    });
    });
  }

  logout() {
    // Ensure Auth0 client instance exists
    this.auth0Client$.subscribe((client: Auth0Client) => {
      // Call method to log out
      client.logout({
        clientId: this._config.clientID,
        logoutParams:{
          returnTo: this._config.logoutUrl,
          federated: true,
        }
      });
    });
  }

  getTokenSilently(): Observable<any> {
    return this.auth0Client$.pipe(
      mergeMap((client: Auth0Client) =>
        from(
          client.getTokenSilently({
            authorizationParams:{
              audience: this._config.audience,
              scope: this._config.scope,
              redirect_uri: this._config.redirectUri
            }
          })
        )
      )
    );
  }

  setupAuth0() {
    this.auth0Client$ = (
      from(
        createAuth0Client({
          domain: this._config.domain,
          clientId: this._config.clientID,
          authorizationParams: {
            redirect_uri: this._config.redirectUri,
            audience: this._config.audience,
            scope: this._config.scope
          }
        })
      )
    ).pipe(
      shareReplay(1), // Every subscription receives the same shared value
      catchError((err) => throwError(() => err))
    );
  }
}


export const AuthGuard : CanActivateFn = (
  next: ActivatedRouteSnapshot,
  state: RouterStateSnapshot
) :Observable<boolean> | Promise<boolean | UrlTree> | boolean => {
  return inject(AuthService).canActivate(next,state);
}